import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getApiUrl } from '../utils/apiConfig';
import { useAccount } from '../contexts/AccountContext';
import { getUserData } from '../utils/userUtils';
import LoadingCard from './LoadingCard';
import './LaunchPage.css';

/**
 * LaunchPage — Main Campaigns Launching board.
 *
 * Lists the account's NOT-YET-LIVE planning-mode campaigns grouped by stage
 * (Plan: Ready for review / Plan: In review / DSP: Ready for review) with a
 * separate "Processing" table below, per the Stellair Campaign Launch design.
 * Pixel-faithful to index.html (the .ws-* list); markup + classes match the
 * design so the scoped CSS port renders identically.
 *
 * Read-only board for now: rows link to the campaign detail page; the row
 * stats are populated where we can read them (ad groups + budget from the
 * launcher result) and show "—" otherwise (issues / match land with the
 * QA-data + reconciliation follow-ups).
 *
 * Gated by the existing `enable-v3-ui-launch` button on the Campaigns page.
 */

// Stage → presentation (status dot/label classes + filter pill), from the design.
const STAGE_META = {
  plan_ready:     { dot: 'ws-dot-ready',   statusCls: 'ws-status-ready',   label: 'Plan: Ready for review', pill: 'ws-pill-ready',   pillLabel: 'plan ready' },
  plan_in_review: { dot: 'ws-dot-review',  statusCls: 'ws-status-review',  label: 'Plan: In review',         pill: 'ws-pill-review',  pillLabel: 'in review' },
  dsp_ready:      { dot: 'ws-dot-live',    statusCls: 'ws-status-live',    label: 'DSP: Ready for review',  pill: 'ws-pill-live',    pillLabel: 'DSP review' },
  stopped:        { dot: 'ws-dot-stopped', statusCls: 'ws-status-stopped', label: 'Stopped',                pill: 'ws-pill-stopped', pillLabel: 'stopped' },
};
// Row / pill order matches the design: DSP review, in review, plan ready;
// stopped runs sink to the bottom of the list.
const LIST_ORDER = ['dsp_ready', 'plan_in_review', 'plan_ready', 'stopped'];

// Mirrors the CLW summary pane: estimated (impression-only) budgets render
// with a leading "~", matching CampaignLaunchWorkflow's `~$X`.
const fmtBudget = (n, estimated) => (n == null ? '—' : (estimated ? '~' : '') + '$' + Number(n).toLocaleString('en-US'));
const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US'));

const LaunchPage = () => {
  const navigate = useNavigate();
  const { getAccountIdToUse } = useAccount();
  const accountId = getAccountIdToUse();
  const [user] = useState(() => getUserData());
  const userId = user?.userId || user?.id;

  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(null); // active stage filter, or null

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await api.get(getApiUrl('/api/v2/campaigns/launch-list'), {
          params: { accountId, userId },
        });
        if (!cancelled) setCampaigns(res.data?.campaigns || []);
      } catch (e) {
        if (!cancelled) setError('Failed to load campaigns.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [accountId, userId]);

  const { listRows, procRows, counts } = useMemo(() => {
    const counts = {};
    for (const c of campaigns) counts[c.stage] = (counts[c.stage] || 0) + 1;
    const proc = campaigns.filter((c) => c.stage === 'processing');
    const list = campaigns
      .filter((c) => c.stage !== 'processing')
      .sort((a, b) => (LIST_ORDER.indexOf(a.stage) - LIST_ORDER.indexOf(b.stage))
        || new Date(b.updatedAt) - new Date(a.updatedAt));
    return { listRows: list, procRows: proc, counts };
  }, [campaigns]);

  const toggleFilter = (stage) => setFilter((f) => (f === stage ? null : stage));

  const visibleList = filter ? listRows.filter((c) => c.stage === filter) : listRows;
  // Main (Plan/DSP) list: shown unless we're filtering to processing, and
  // hidden in the unfiltered view when there are no Plan/DSP campaigns (so a
  // board of only in-flight campaigns doesn't render an empty "in review" list).
  const showMainList = filter !== 'processing' && (Boolean(filter) || listRows.length > 0);
  // Processing section: shown when filtering to processing (with its own empty
  // state) or, in the unfiltered view, whenever there's something processing.
  const showProcSection = filter === 'processing' || (!filter && procRows.length > 0);
  const visibleProc = filter && filter !== 'processing' ? [] : procRows;

  // Open the campaign from the board. `from=launch` routes to the
  // campaign-details variant (LaunchCampaignDetails), which picks its view
  // from the campaign's stage — plan review for plan-stage rows, DSP
  // reconcile for launched ones — and links to CLW (still the home of the
  // edit/approve flow) when there's no parsed plan to show.
  const openCampaign = (c) => navigate(`/campaign/v3/start?campaign=${encodeURIComponent(c.id)}&from=launch`);

  const renderRow = (c) => {
    const meta = STAGE_META[c.stage];
    if (!meta) return null;
    const isDsp = c.stage === 'dsp_ready';
    return (
      <div
        key={c.id}
        className={`ws-row ${meta.statusCls}`}
        onClick={() => openCampaign(c)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') openCampaign(c); }}
      >
        <div className="ws-status">
          <div className={`ws-dot ${meta.dot}`} />
          <div className="ws-status-lbl">{meta.label}</div>
        </div>
        <div className="ws-name" title={c.destination ? `${c.name} · ${c.destination}` : c.name}>
          {c.name}
          {c.destination && <span className="ws-client"> · {c.destination}</span>}
        </div>
        <div className="ws-stats">
          <div className="ws-stat"><b>{fmtNum(c.adGroups)}</b></div>
          <div className="ws-stat">
            <span className="ws-stat-lbl">{isDsp ? 'in flight' : 'budget'}</span>
            <b>{fmtBudget(c.budget, c.budgetEstimated)}</b>
          </div>
          <div className={`ws-stat ${c.issues > 0 ? 'ws-stat-warn' : 'ws-stat-zero'}`}>
            <span className="ws-stat-lbl">{isDsp ? 'discrepancies' : 'issues'}</span>
            <b>{fmtNum(c.issues)}</b>
          </div>
          <div className="ws-stat ws-stat-ok">
            <span className="ws-stat-lbl">{isDsp ? 'match' : 'auto-pop.'}</span>
            <b>{c.matched == null ? '—' : c.matched}</b>
          </div>
        </div>
        <div className="ws-action"><span className="ws-arrow">→</span></div>
      </div>
    );
  };

  return (
    <div className="launch-list-root">
      {error && <div className="ll-error">{error}</div>}

      {isLoading ? (
        <LoadingCard
          title="Loading campaigns…"
          sub="Fetching your not-yet-live campaigns and their current stage."
        />
      ) : (
        <>
          {/* Main workspace — head lives INSIDE the margined card, per design */}
          <div id="workspace">
            <div className="ws-head">
              <span className="ws-head-lbl">My campaigns</span>
              <span className="ws-head-counts"><strong>{listRows.filter((c) => c.stage !== 'stopped').length}</strong> active ·</span>
              <span className="ws-head-pills">
                {LIST_ORDER.map((stage) => (
                  <button
                    key={stage}
                    type="button"
                    className={`ws-head-pill ${STAGE_META[stage].pill} ${filter === stage ? 'filter-active' : ''}`}
                    onClick={() => toggleFilter(stage)}
                  >
                    {(counts[stage] || 0)} {STAGE_META[stage].pillLabel}
                    <span className="pill-x" onClick={(e) => { e.stopPropagation(); toggleFilter(stage); }}>×</span>
                  </button>
                ))}
                <button
                  type="button"
                  className={`ws-head-pill ws-pill-proc ${filter === 'processing' ? 'filter-active' : ''}`}
                  onClick={() => toggleFilter('processing')}
                >
                  {(counts.processing || 0)} processing
                  <span className="pill-x" onClick={(e) => { e.stopPropagation(); toggleFilter('processing'); }}>×</span>
                </button>
              </span>
              <div className="ws-spacer" />
              <button className="ws-new" onClick={() => navigate('/campaigns')}>+ New campaign</button>
            </div>
            {campaigns.length === 0 ? (
              <div className="ll-empty">No campaigns in launch review. Campaigns awaiting review or being processed will appear here.</div>
            ) : showMainList && (
              <div className="ws-list" id="ws-list">
                <div className="ws-col-header">
                  <div className="ws-col-hdr ws-col-hdr-status">Status</div>
                  <div className="ws-col-hdr ws-col-hdr-name">Campaign</div>
                  <div className="ws-col-hdr-stats">
                    <div className="ws-col-hdr ws-col-hdr-stat">Ad groups</div>
                    <div className="ws-col-hdr ws-col-hdr-stat">Budget / In flight</div>
                    <div className="ws-col-hdr ws-col-hdr-stat">Issues</div>
                    <div className="ws-col-hdr ws-col-hdr-stat">Match / Auto-pop.</div>
                  </div>
                  <div className="ws-col-hdr ws-col-hdr-action" />
                </div>
                {visibleList.length === 0
                  ? <div className="ll-empty">No campaigns in this stage.</div>
                  : visibleList.map(renderRow)}
              </div>
            )}
          </div>

          {campaigns.length > 0 && showProcSection && (
            <div id="workspace-proc">
              <div className="ws-head">
                <span className="ws-head-lbl">Processing</span>
                <span className="ws-head-counts"><strong>{procRows.length}</strong> uploading &amp; parsing</span>
              </div>
              {visibleProc.length === 0 ? (
                <div className="ll-empty">No campaigns processing right now.</div>
              ) : (
                <div className="ws-proc-list">
                  <div className="ws-col-header">
                    <div className="ws-col-hdr">Status</div>
                    <div className="ws-col-hdr">Campaign</div>
                    <div className="ws-col-hdr">Progress</div>
                    <div className="ws-col-hdr">ETA</div>
                  </div>
                  {visibleProc.map((c) => (
                    <div
                      key={c.id}
                      className="ws-row ws-status-proc"
                      onClick={() => openCampaign(c)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') openCampaign(c); }}
                    >
                      <div className="ws-status">
                        <div className="ws-dot ws-dot-proc" />
                        <div className="ws-status-lbl">Processing</div>
                      </div>
                      <div className="ws-name" title={c.destination ? `${c.name} · ${c.destination}` : c.name}>
                        {c.name}
                        {c.destination && <span className="ws-client"> · {c.destination}</span>}
                      </div>
                      <div className="ws-stat ws-stat-progress">
                        <span className="ws-stat-lbl">progress</span>
                        <div className="ws-prog-bar"><div className="ws-prog-fill" /></div>
                      </div>
                      <div className="ws-stat"><span className="ws-stat-lbl">eta</span><b>—</b></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LaunchPage;
